import { Box, Typography, Button, TextField, Alert } from "@mui/material";
import { Add } from "@mui/icons-material";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import React, { useEffect, useState, useRef, useMemo } from "react";
import { defaultParams } from "../../App";
import { useCurrencyFormatter } from "../../hooks/useCurrencyFormatter";
import AcrInputs from "./AcrInputs";
import TermLengthEntry from "./TermLengthEntry";
import { CurrencyField } from "./CurrencyField";
import FormWrapper from "./FormWrapper";
import {
  SimulationFormProps,
  SimulationFormInputs,
  SimulationFormParams,
} from "../../types/forms";
import SubmitButton from "./SubmitButton";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";

// Helper function to calculate monthly payment from principal, rate, and term
function calculateMonthlyPayment(
  principal: number,
  interestRate: number,
  termYears: number,
): number {
  if (principal <= 0 || termYears <= 0) return 0;
  const monthlyRate = interestRate / 12 / 100;
  const termMonths = termYears * 12;

  if (monthlyRate === 0) {
    return Math.round((principal / termMonths) * 100) / 100;
  }

  const payment =
    (principal * monthlyRate * Math.pow(1 + monthlyRate, termMonths)) /
    (Math.pow(1 + monthlyRate, termMonths) - 1);
  return Math.round(payment * 100) / 100;
}

const demoParams: SimulationFormInputs = {
  principal: 250000,
  interestOnly: false,
  termYears: 25,
  interestRate: 4.5,
  desiredMonthlyPayment: undefined,
  additionalCapitalRepayments: true,
  dealLength: 24,
  maxAffordable: 0, // Will be calculated from shortest term
  savingsInterestRate: 3.5,
  saveAtStart: false,
  additionalCapitalRepaymentPercentageLimit: 10,
  reduceTermOnAdditionalCapitalRepayment: false,
  savingsAccounts: [],
};

export default function PaymentFlexibilitySimulationForm({
  model,
  onSubmit,
  onCancel,
  isEditing,
  editingIndex,
}: SimulationFormProps) {
  const [localIndices, setLocalIndices] = useState<number[]>([]);
  const [isInitialized, setIsInitialized] = useState<boolean>(false);
  const hasInitialized = useRef<boolean>(false);

  const c = useCurrencyFormatter();

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = localIndices.findIndex(
        (idx) => `payment-${idx}` === active.id,
      );
      const newIndex = localIndices.findIndex(
        (idx) => `payment-${idx}` === over.id,
      );

      if (oldIndex !== -1 && newIndex !== -1) {
        model.moveSimulation(oldIndex, newIndex);
      }
    }
  };

  useEffect(() => {
    if (!isEditing && !hasInitialized.current) {
      hasInitialized.current = true;
      const newSim1: SimulationFormParams = {
        inputs: {
          ...defaultParams,
          additionalCapitalRepayments: true,
          termYears: undefined,
          desiredMonthlyPayment: undefined,
        },
        updated: true,
      };
      const newSim2: SimulationFormParams = {
        inputs: {
          ...defaultParams,
          additionalCapitalRepayments: true,
          termYears: undefined,
          desiredMonthlyPayment: undefined,
        },
        updated: true,
      };
      model.addSimulation(newSim1);
      model.addSimulation(newSim2);
      setLocalIndices([0, 1]);
      setIsInitialized(true);
    } else if (isEditing && !hasInitialized.current) {
      hasInitialized.current = true;
      const existingIndices = model.simulations.map((_, i) => i);
      setLocalIndices(existingIndices);
      setIsInitialized(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCancel = () => {
    if (!isEditing && localIndices.length > 0) {
      [...localIndices].reverse().forEach((idx) => {
        model.removeSimulation(idx);
      });
    }
    onCancel();
  };

  const handleAddSimulation = () => {
    const params = model.simulations[localIndices[0]]?.inputs || defaultParams;
    const newSim: SimulationFormParams = {
      inputs: {
        ...params,
        additionalCapitalRepayments: true,
        termYears: undefined,
        desiredMonthlyPayment: undefined,
      },
      updated: true,
    };
    model.addSimulation(newSim);
    setLocalIndices([...localIndices, model.simulations.length]);
  };

  const handleRemoveSimulation = (indexInLocalArray: number) => {
    if (localIndices.length <= 2) return;

    const actualIndex = localIndices[indexInLocalArray];
    model.removeSimulation(actualIndex);

    const newLocalIndices = localIndices
      .filter((_, i) => i !== indexInLocalArray)
      .map((idx) => (idx > actualIndex ? idx - 1 : idx));

    setLocalIndices(newLocalIndices);
  };

  const handleDemo = () => {
    if (localIndices.length > 0) {
      [...localIndices].reverse().forEach((idx) => {
        model.removeSimulation(idx);
      });
    }

    const newSim1: SimulationFormParams = {
      inputs: { ...demoParams, termYears: 25 },
      updated: true,
    };
    const newSim2: SimulationFormParams = {
      inputs: { ...demoParams, termYears: 30 },
      updated: true,
    };
    model.addSimulation(newSim1);
    model.addSimulation(newSim2);
    setLocalIndices([
      model.simulations.length - 2,
      model.simulations.length - 1,
    ]);
  };

  // Calculate monthly budget based on shortest term length
  // Create a serialized version of all term years to use as a dependency
  const allTermYears = localIndices
    .map((idx) => model.simulations[idx]?.inputs.termYears || 0)
    .join(",");

  // Also track total simulations count to detect deletions
  const simulationsCount = model.simulations.length;

  const calculatedMonthlyBudget = useMemo(() => {
    if (localIndices.length === 0) return 0;

    const firstSim = model.simulations[localIndices[0]]?.inputs;
    if (!firstSim) return 0;

    const principal = firstSim.principal || 0;
    const interestRate = firstSim.interestRate || 0;

    // Find the shortest term among all simulations
    const termYears = localIndices
      .map((idx) => model.simulations[idx]?.inputs.termYears || 0)
      .filter((t) => t > 0);

    if (termYears.length === 0) return 0;

    const shortestTerm = Math.min(...termYears);
    return calculateMonthlyPayment(principal, interestRate, shortestTerm);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    localIndices.length,
    simulationsCount,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    model.simulations[localIndices[0]]?.inputs.principal,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    model.simulations[localIndices[0]]?.inputs.interestRate,
    allTermYears,
  ]);

  // Update maxAffordable across all simulations when calculated budget changes
  // Also disable ACRs for the shortest term simulation (where budget equals payment)
  // Use localIndicesKey and allTermYears as dependencies to detect when simulations are removed/changed
  const localIndicesKey = localIndices.join(",");

  useEffect(() => {
    if (localIndices.length > 0) {
      // Find the shortest term
      const termYears = localIndices
        .map((idx) => ({
          idx,
          termYears: model.simulations[idx]?.inputs.termYears || 0,
        }))
        .filter((t) => t.termYears > 0);

      const shortestTermYear =
        termYears.length > 0
          ? Math.min(...termYears.map((t) => t.termYears))
          : 0;

      localIndices.forEach((idx) => {
        const sim = model.simulations[idx];
        if (!sim) return;

        const currentMax = sim.inputs.maxAffordable;
        const currentTermYears = sim.inputs.termYears || 0;
        const isShortestTerm =
          currentTermYears > 0 && currentTermYears === shortestTermYear;

        // For shortest term, disable ACRs since budget equals payment
        const shouldHaveAcr = !isShortestTerm;
        const updates: Partial<SimulationFormInputs> = {};

        // Always update maxAffordable (even if 0) to ensure consistency
        if (currentMax !== calculatedMonthlyBudget) {
          updates.maxAffordable = calculatedMonthlyBudget;
        }

        if (sim.inputs.additionalCapitalRepayments !== shouldHaveAcr) {
          updates.additionalCapitalRepayments = shouldHaveAcr;
        }

        if (Object.keys(updates).length > 0) {
          model.updateSimulation(idx, {}, updates);
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [calculatedMonthlyBudget, localIndicesKey, allTermYears, model]);

  if (!isInitialized) {
    return null;
  }

  if (localIndices == null) {
    return null;
  }

  return (
    <FormWrapper onSubmit={onSubmit}>
      {(error) => (
        <>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
            }}
          >
            <Typography variant="h6">
              {isEditing ? "Update Simulations" : "Create Simulations"}
            </Typography>
            <Button
              startIcon={<AutoFixHighIcon />}
              onClick={handleDemo}
              variant="outlined"
              size="small"
            >
              Demo
            </Button>
          </Box>

          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ mb: 1 }}>
              <strong>What is Payment Flexibility?</strong>
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              This mode explores the trade-off between committing to a shorter
              term (higher monthly payments) versus a longer term (lower monthly
              payments) while using the difference for additional capital
              repayments (ACRs).
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              The monthly budget is automatically set to the payment required
              for the shortest term. For longer terms, the difference between
              the budget and the lower required payment goes into a reserve
              account and is used for ACRs when conditions allow.
            </Typography>
            <Typography variant="body2">
              A longer term means lower committed payments, giving more
              flexibility if finances become tight. This simulation helps
              understand the cost of that flexibility.
            </Typography>
          </Alert>

          <CurrencyField
            label="Mortgage Debt"
            value={model.simulations[localIndices[0]]?.inputs.principal}
            onChange={(val) => {
              localIndices.forEach((idx) => {
                model.updateSimulation(idx, {}, { principal: val });
              });
            }}
            fullWidth
            margin="normal"
            required
            greaterThanZero
          />

          <TextField
            label="Interest Rate (%)"
            type="number"
            slotProps={{ htmlInput: { step: 0.01, min: 0 } }}
            value={
              model.simulations[localIndices[0]]?.inputs.interestRate ?? ""
            }
            onChange={(e) => {
              const val =
                e.target.value === "" ? undefined : parseFloat(e.target.value);
              localIndices.forEach((idx) => {
                model.updateSimulation(idx, {}, { interestRate: val });
              });
            }}
            fullWidth
            margin="normal"
            required
          />

          <AcrInputs
            additionalCapitalRepayments={true}
            dealLength={model.simulations[localIndices[0]]?.inputs.dealLength}
            maxAffordable={
              model.simulations[localIndices[0]]?.inputs.maxAffordable
            }
            savingsInterestRate={
              model.simulations[localIndices[0]]?.inputs.savingsInterestRate
            }
            saveAtStart={model.simulations[localIndices[0]]?.inputs.saveAtStart}
            additionalCapitalRepaymentPercentageLimit={
              model.simulations[localIndices[0]]?.inputs
                .additionalCapitalRepaymentPercentageLimit
            }
            reduceTermOnAdditionalCapitalRepayment={
              model.simulations[localIndices[0]]?.inputs
                .reduceTermOnAdditionalCapitalRepayment
            }
            skipAdditionalCapitalRepayments={false}
            interestOnly={
              model.simulations[localIndices[0]]?.inputs.interestOnly
            }
            onChange={(updates: Partial<SimulationFormInputs>) => {
              localIndices.forEach((idx) => {
                model.updateSimulation(idx, {}, updates);
              });
            }}
            alwaysExpanded={true}
            hideMonthlyBudget={true}
            hideSkipAcr={true}
          />

          <Box sx={{ mt: 3 }}>
            <Typography variant="h6" sx={{ mb: 1 }}>
              Term Length Options
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              The term length determines the monthly payment committed to the
              lender. A longer term results in lower monthly payments, providing
              more flexibility if income drops. A shorter term means higher
              payments but potentially lower total cost.
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              The <strong>shortest term</strong> sets the monthly budget for all
              simulations. For longer terms, the difference between the budget
              and the required payment goes into a reserve for additional
              capital repayments.
            </Typography>
            {calculatedMonthlyBudget > 0 && (
              <Alert severity="success" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  <strong>Monthly Budget:</strong> {c(calculatedMonthlyBudget)}{" "}
                  (based on shortest term)
                </Typography>
              </Alert>
            )}
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={localIndices.map((idx) => `payment-${idx}`)}
                strategy={verticalListSortingStrategy}
              >
                {localIndices.map((actualIndex, i) => {
                  const formInputs = model.simulations[actualIndex]?.inputs || {
                    ...defaultParams,
                    termYears: 0,
                  };
                  return (
                    <TermLengthEntry
                      key={actualIndex}
                      id={`payment-${actualIndex}`}
                      index={i}
                      value={formInputs.termYears}
                      onChange={(val) =>
                        model.updateSimulation(
                          actualIndex,
                          {},
                          { termYears: val, desiredMonthlyPayment: undefined },
                        )
                      }
                      onRemove={() => handleRemoveSimulation(i)}
                      canRemove={localIndices.length > 2}
                      autoFocus={editingIndex === actualIndex}
                    />
                  );
                })}
              </SortableContext>
            </DndContext>
            <Button
              startIcon={<Add />}
              onClick={handleAddSimulation}
              variant="outlined"
              fullWidth
              sx={{ mt: 1 }}
            >
              Add Simulation
            </Button>
          </Box>

          <SubmitButton
            isEditing={isEditing}
            multipleSimulations={true}
            error={error}
            onCancel={handleCancel}
          />
        </>
      )}
    </FormWrapper>
  );
}
